import React, { useEffect, useState } from "react";
import Layout from "../pages/Layout";
import Advertise from "../pages/Advertise";

const Emi = () => {
  const [loanAmount, setLoanAmount] = useState("");
  const [annualInterestRate, setAnnualInterestRate] = useState("");
  const [loanTerm, setLoanTerm] = useState("");
  const [monthlyEMI, setMonthlyEMI] = useState("₹0.00");
  const [totalInterest, setTotalInterest] = useState("₹0.00");

  // Function to calculate EMI and total interest
  const calculateEMI = () => {
    const P = parseFloat(loanAmount) || 0; // Loan amount
    const annualRate = parseFloat(annualInterestRate) || 0; // Annual interest rate
    const n = parseInt(loanTerm) || 0; // Loan term in months

    if (P === 0 || annualRate === 0 || n === 0) {
      setMonthlyEMI("₹0.00");
      setTotalInterest("₹0.00");
      return;
    }

    const r = annualRate / 100 / 12; // Monthly interest rate

    // EMI calculation formula
    const EMI = (P * r * Math.pow(1 + r, n)) / (Math.pow(1 + r, n) - 1);

    // Total Interest calculation
    const totalPaid = EMI * n; // Total amount paid over the loan term
    const interestPaid = totalPaid - P; // Total interest paid

    setMonthlyEMI(`₹${EMI.toFixed(2)}`);
    setTotalInterest(`₹${interestPaid.toFixed(2)}`);
  };

  // Use effect hook to recalculate EMI and interest whenever inputs change
  useEffect(() => {
    calculateEMI();

    // This will initialize or reinitialize all popovers on the page whenever the component renders
    const popoverTriggerList = [].slice.call(
      document.querySelectorAll('[data-bs-toggle="popover"]')
    );

    // Clean up any existing popover instances
    popoverTriggerList.forEach((popoverTriggerEl) => {
      const popover = window.bootstrap.Popover.getInstance(popoverTriggerEl); // Check if instance already exists
      if (popover) {
        popover.dispose(); // Dispose of existing popover
      }
    });

    // Re-initialize popovers
    popoverTriggerList.forEach((popoverTriggerEl) => {
      new window.bootstrap.Popover(popoverTriggerEl); // Re-initialize popovers
    });
  }, [loanAmount, annualInterestRate, loanTerm]);

  return (
    <Layout
      title="EMI (Equated Monthly Installment) Calculator"
      path="equated-monthly-installment"
    >
      <section
        className="emi-calculator-section section-padding"
        id="emi-calculator"
      >
        <div className="container">
          <div className="row">
            {/* EMI Calculator Section (75% space) */}
            <div className="col-lg-9 col-md-8 mb-4">
              <div
                className="p-4 border rounded shadow"
                style={{ border: "1px solid black" }}
              >
                <h3 className="text-center mb-4">EMI Calculator</h3>
                <div className="container">
                  {/* Input Fields for EMI Calculation */}
                  <div className="row gy-3">
                    {/* Loan Amount */}
                    <div className="col-md-6 col-lg-4">
                      <label htmlFor="loanAmount" className="form-label">
                        Loan Amount
                        <span
                          className="ms-2 text-muted"
                          data-bs-toggle="popover"
                          data-bs-html="true"
                          data-bs-placement="top"
                          data-bs-content="<strong>Loan Amount:</strong> The total sum of money borrowed from the lender."
                          style={{ cursor: "pointer" }}
                        >
                          <i className="bi bi-info-circle"></i>
                        </span>
                      </label>
                      <input
                        type="number"
                        className="form-control"
                        id="loanAmount"
                        value={loanAmount}
                        step="0.01"
                        placeholder="Enter loan amount"
                        onChange={(e) => setLoanAmount(e.target.value)} // Update state on change
                      />
                      <small className="form-text text-muted">in ₹</small>
                    </div>

                    {/* Annual Interest Rate */}
                    <div className="col-md-6 col-lg-4">
                      <label
                        htmlFor="annualInterestRate"
                        className="form-label"
                      >
                        Annual Interest Rate
                        <span
                          className="ms-2 text-muted"
                          data-bs-toggle="popover"
                          data-bs-html="true"
                          data-bs-placement="top"
                          data-bs-content="<strong>Annual Interest Rate:</strong> The yearly interest charged on the loan amount, expressed as a percentage."
                          style={{ cursor: "pointer" }}
                        >
                          <i className="bi bi-info-circle"></i>
                        </span>
                      </label>
                      <input
                        type="number"
                        className="form-control"
                        id="annualInterestRate"
                        value={annualInterestRate}
                        step="0.01"
                        placeholder="Enter annual interest rate"
                        onChange={(e) => setAnnualInterestRate(e.target.value)} // Update state on change
                      />
                      <small className="form-text text-muted">
                        in percentage
                      </small>
                    </div>

                    {/* Loan Term (in months) */}
                    <div className="col-md-6 col-lg-4">
                      <label htmlFor="loanTerm" className="form-label">
                        Loan Term
                        <span
                          className="ms-2 text-muted"
                          data-bs-toggle="popover"
                          data-bs-html="true"
                          data-bs-placement="top"
                          data-bs-content="<strong>Loan Term:</strong> The duration over which the loan will be repaid, typically in months."
                          style={{ cursor: "pointer" }}
                        >
                          <i className="bi bi-info-circle"></i>
                        </span>
                      </label>
                      <input
                        type="number"
                        className="form-control"
                        id="loanTerm"
                        value={loanTerm}
                        min="1"
                        placeholder="Enter loan term in months"
                        onChange={(e) => setLoanTerm(e.target.value)} // Update state on change
                      />
                      <small className="form-text text-muted">in months</small>
                    </div>
                  </div>

                  {/* Result Section */}
                  <div className="mt-4">
                    <div className="p-3 text-center">
                      <h6>
                        Monthly EMI (₹):{" "}
                        <span className="fw-bold ms-3">{monthlyEMI}</span>
                      </h6>
                      <h6>
                        Total Interest Paid (₹):{" "}
                        <span className="fw-bold ms-3">{totalInterest}</span>
                      </h6>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Advertisement Section (25% space) */}
            <Advertise />
          </div>
        </div>
      </section>
      <section className="introduction-section" id="introduction">
        <div className="container">
          <div className="row">
            <div className="col-lg-12">
              <div className="p-5">
                <h3 className="text-center mb-5">
                  Introduction to the EMI Calculator
                </h3>

                {/* What is an EMI Calculator? */}
                <h4 className="mt-3 mb-3">What is an EMI Calculator?</h4>
                <p>
                  An <strong>EMI Calculator</strong> is a tool designed to help
                  individuals and businesses estimate their monthly loan
                  repayments. It calculates the Equated Monthly Installment
                  (EMI) based on the loan amount, the interest rate, and the
                  loan term, making it easy to understand the financial
                  obligations involved in taking a loan.
                </p>

                {/* Why Use an EMI Calculator? */}
                <h4 className="mt-4 mb-3">Why Use an EMI Calculator?</h4>
                <p>Using an EMI calculator provides several benefits:</p>
                <ul>
                  <li>
                    <strong>Accurate Calculations:</strong> Offers a precise
                    estimate of monthly payments, reducing the chances of
                    miscalculations.
                  </li>
                  <li>
                    <strong>Time-Saving:</strong> Speeds up the process of
                    understanding loan obligations without manual computations.
                  </li>
                  <li>
                    <strong>Financial Planning:</strong> Helps users plan their
                    budgets effectively by understanding the monthly payment in
                    advance.
                  </li>
                  <li>
                    <strong>Loan Comparison:</strong> Enables users to compare
                    different loan options by varying input parameters like the
                    interest rate and term.
                  </li>
                </ul>

                {/* How to Use an EMI Calculator? */}
                <h4 className="mt-4 mb-3">How to Use an EMI Calculator?</h4>
                <p>To use an EMI calculator effectively, follow these steps:</p>
                <ul>
                  <li>
                    Enter the <strong>loan amount</strong> (principal) you wish
                    to borrow.
                  </li>
                  <li>
                    Input the <strong>annual interest rate</strong>, which will
                    be converted to a monthly rate.
                  </li>
                  <li>
                    Provide the <strong>loan term</strong> in months to
                    understand the duration of repayment.
                  </li>
                  <li>
                    The calculator will then display the{" "}
                    <strong>monthly EMI</strong> for the given parameters.
                  </li>
                </ul>
                <p>
                  With these simple inputs, users can easily assess their
                  potential monthly financial commitment.
                </p>

                {/* What Factors Affect EMI Calculation? */}
                <h4 className="mt-4 mb-3">
                  What Factors Affect EMI Calculation?
                </h4>
                <p>
                  Several factors influence EMI calculations and the final
                  monthly payment:
                </p>
                <ul>
                  <li>
                    <strong>Loan Amount (P):</strong> The principal sum
                    borrowed.
                  </li>
                  <li>
                    <strong>Interest Rate (r):</strong> The rate at which
                    interest is charged annually, affecting monthly payments.
                  </li>
                  <li>
                    <strong>Loan Term (n):</strong> The number of months over
                    which the loan is repaid; longer terms typically result in
                    smaller EMIs but higher overall interest.
                  </li>
                </ul>

                {/* Limitations of an EMI Calculator */}
                <h4 className="mt-4 mb-3">Limitations of an EMI Calculator</h4>
                <p>
                  While an EMI calculator is a powerful tool, it has certain
                  limitations:
                </p>
                <ul>
                  <li>
                    <strong>Assumes Constant Interest Rate:</strong> The tool
                    works on a fixed interest rate and may not account for
                    variable rates that can change over time.
                  </li>
                  <li>
                    <strong>No Consideration for Fees:</strong> Additional
                    charges like processing fees or late payment penalties are
                    not included in EMI calculations.
                  </li>
                </ul>

                {/* How to Interpret EMI Results? */}
                <h4 className="mt-4 mb-3">How to Interpret EMI Results?</h4>
                <p>
                  Understanding the results from an EMI calculator is crucial:
                </p>
                <ul>
                  <li>
                    <strong>Monthly EMI:</strong> The fixed amount you need to
                    pay each month towards loan repayment.
                  </li>
                  <li>
                    <strong>Total Payable Amount:</strong> The overall sum to be
                    repaid, including both principal and interest.
                  </li>
                  <li>
                    <strong>Financial Readiness:</strong> Helps determine if the
                    monthly EMI fits within your budget, ensuring financial
                    stability.
                  </li>
                </ul>

                <p>
                  In conclusion, the <strong>EMI Calculator</strong> is an
                  invaluable tool for anyone considering a loan, providing quick
                  insights and aiding in comprehensive financial planning. With
                  this tool, users can approach loan commitments with better
                  understanding and preparedness.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default Emi;
